import axios from 'axios';

// Hardcoded API URL based on hostname
//TODO: remove this hardcoded API URL later
const getApiUrl = () => {
  const hostname = window.location.hostname;

  // Production domains
  if (hostname === 'lima.ac' || hostname.includes('lima.ac')) {
    return 'https://lima.ac/api';
  }
  if (hostname === 'lima.pitech.ca' || hostname.includes('lima.pitech.ca')) {
    return 'https://lima.pitech.ca/api';
  }
  if (hostname === 'bagbox.ca' || hostname.includes('bagbox.ca')) {
    return 'https://bagbox.ca/api';
  }

  // Development/localhost
  return 'http://localhost:80/api';
};

const API_URL = getApiUrl();

/**
 * Scheduled Posts API Service
 * Handles both manual and AI-powered scheduled blog posts
 */
class ScheduledPostsAPI {
  /**
   * Get all scheduled posts
   */
  async getScheduledPosts() {
    try {
      const response = await axios.get(`${API_URL}/scheduled-posts`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching scheduled posts:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get single scheduled post by ID
   */
  async getScheduledPost(id) {
    try {
      const response = await axios.get(`${API_URL}/scheduled-posts/${id}`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching scheduled post:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Create scheduled post
   * MODE 1: Manual scheduling - pre-written content, one-time publish
   * MODE 2: AI-powered recurring - AI generates content automatically
   */
  async createScheduledPost(postData) {
    try {
      const response = await axios.post(
        `${API_URL}/scheduled-posts`,
        postData,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating scheduled post:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Create multiple scheduled posts at once (batch)
   */
  async createScheduledPosts(postsArray) {
    try {
      const response = await axios.post(
        `${API_URL}/scheduled-posts/batch`,
        { posts: postsArray },
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating scheduled posts:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Create AI Schedule - Recurring automatic post generation
   * This saves a schedule configuration that will be executed by backend cron job
   */
  async createAISchedule(scheduleConfig) {
    try {
      const response = await axios.post(
        `${API_URL}/scheduled-posts/ai-schedule`,
        scheduleConfig,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating AI schedule:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get all AI schedules
   */
  async getAISchedules() {
    try {
      const response = await axios.get(`${API_URL}/scheduled-posts/ai-schedules`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching AI schedules:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete AI schedule
   */
  async deleteAISchedule(id) {
    try {
      const response = await axios.delete(`${API_URL}/scheduled-posts/ai-schedule/${id}`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting AI schedule:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Toggle AI schedule active status
   */
  async toggleAISchedule(id) {
    try {
      const response = await axios.patch(
        `${API_URL}/scheduled-posts/ai-schedule/${id}/toggle`,
        {},
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error toggling AI schedule:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update scheduled post
   */
  async updateScheduledPost(id, updates) {
    try {
      const response = await axios.put(
        `${API_URL}/scheduled-posts/${id}`,
        updates,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating scheduled post:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete scheduled post
   */
  async deleteScheduledPost(id) {
    try {
      const response = await axios.delete(`${API_URL}/scheduled-posts/${id}`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting scheduled post:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Toggle active status
   */
  async toggleScheduledPost(id) {
    try {
      const response = await axios.patch(
        `${API_URL}/scheduled-posts/${id}/toggle`,
        {},
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error toggling scheduled post:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Manually trigger execution (for testing)
   */
  async executeScheduledPost(id) {
    try {
      const response = await axios.post(
        `${API_URL}/scheduled-posts/${id}/execute-now`,
        {},
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error executing scheduled post:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get execution history
   */
  async getExecutionHistory(id) {
    try {
      const response = await axios.get(`${API_URL}/scheduled-posts/${id}/history`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching execution history:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Trigger all schedulers manually (admin only)
   */
  async triggerAllSchedulers() {
    try {
      const response = await axios.post(
        `${API_URL}/scheduled-posts/trigger/all`,
        {},
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error triggering schedulers:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get auth headers
   */
  getHeaders() {
    const token = localStorage.getItem('token');
    const hostname = window.location.hostname;

    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
      'x-tenant-host': hostname // Required for multi-tenancy
    };
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    if (error.response) {
      return {
        success: false,
        message: error.response.data.message || 'An error occurred',
        error: error.response.data.error,
        status: error.response.status
      };
    } else if (error.request) {
      return {
        success: false,
        message: 'No response from server',
        error: 'Network error'
      };
    } else {
      return {
        success: false,
        message: error.message || 'An error occurred',
        error: error.message
      };
    }
  }
}

export default new ScheduledPostsAPI();

